/**
 * PricingRuleAppliesToWidget — friendly, editable replacement for the ERP-ish
 * "Apply Rule On" child-table grid in the Pricing Rule Details view.
 *
 * Keeps the full function (manage which products / groups / brands a rule targets,
 * plus warehouse + mixed/cumulative flags) but presents it the friendly way:
 *  - Item Code  → product cards (thumbnail + name, tap-through, add via search, remove)
 *  - Item Group → group chips (add via search, remove)
 *  - Brand      → brand chips (add via search, remove)
 *  - Transaction→ "whole cart" note
 * Advanced flags live in a subtle disclosure so nothing is lost.
 *
 * Edits stage through onFieldChange (the field-view's native Save persists them),
 * so nothing writes to a live rule until the owner clicks Save.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Package, X, ChevronRight, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { usePricingRuleItems } from "./usePricingRuleCoupons";
import { NUM_INPUT_CLASS } from "./styles";

const pick = (th: boolean, t: string, e: string) => (th ? t : e);

type Row = Record<string, any>;

export function PricingRuleAppliesToWidget({ formData, onFieldChange, disabled, isSaving }: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const th = language === "th";
  const ro = disabled || isSaving;
  const applyOn = (formData.apply_on as string) || "Transaction";
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ----- Item Code (products) -----
  const items = (formData.items ?? []) as Row[];
  const codes = items.map((i) => i.item_code).filter(Boolean) as string[];
  const { data: resolved } = usePricingRuleItems(codes, applyOn === "Item Code" && codes.length > 0);
  const byCode = new Map((resolved ?? []).map((it) => [it.item_code, it]));

  const addRow = (field: "items" | "item_groups" | "brands", key: string, docKey: string, value: string) => {
    if (!value) return;
    const list = (formData[field] ?? []) as Row[];
    if (list.some((r) => r[key] === value)) return;
    onFieldChange(field, [...list, { [key]: value, doctype: docKey }]);
  };
  const removeRow = (field: "items" | "item_groups" | "brands", key: string, value: string) => {
    const list = (formData[field] ?? []) as Row[];
    onFieldChange(field, list.filter((r) => r[key] !== value));
  };

  // How many targets are listed — the "combined vs per-product" choice only
  // matters when the rule targets more than one product/group/brand.
  const targetCount =
    applyOn === "Item Code"
      ? codes.length
      : applyOn === "Item Group"
        ? ((formData.item_groups ?? []) as Row[]).length
        : applyOn === "Brand"
          ? ((formData.brands ?? []) as Row[]).length
          : 0;
  const minQty = Number(formData.min_qty) || 0;
  const maxQty = Number(formData.max_qty) || 0;
  const maxAmt = Number(formData.max_amt) || 0;
  const mixed = !!formData.mixed_conditions;

  const title = pick(th, "สินค้าที่ร่วมโปรโมชัน", "Products in this promotion");

  return (
    <div className="px-5 pt-6 pb-5">
      <SectionHeader label={title} />

      {applyOn === "Item Code" && (
        <div className="mt-3 space-y-2">
          {codes.length === 0 && (
            <p className="rounded-lg border border-dashed border-border px-3 py-3 text-[13px] text-gray-500 dark:text-gray-400">
              {pick(th, "ยังไม่ได้เลือกสินค้า", "No products selected yet")}
            </p>
          )}
          {codes.map((code) => {
            const it = byCode.get(code);
            return (
              <div
                key={code}
                className="group flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 transition-colors hover:border-sky-300 dark:hover:border-sky-500/40"
              >
                {it?.image ? (
                  <img src={it.image} alt={it.item_name || code} className="h-10 w-10 shrink-0 rounded-lg object-cover ring-1 ring-border/60" />
                ) : (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted ring-1 ring-border/60">
                    <Package className="h-4 w-4 text-muted-foreground" />
                  </span>
                )}
                <Link to={`/inventory/item/${encodeURIComponent(code)}`} className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-medium text-gray-900 dark:text-gray-100">{it?.item_name || code}</div>
                  <div className="truncate font-mono text-[11px] text-gray-400 dark:text-gray-500">{code}</div>
                </Link>
                <Link
                  to={`/inventory/item/${encodeURIComponent(code)}`}
                  className="shrink-0 text-gray-300 transition-colors hover:text-sky-500 dark:text-gray-600"
                  aria-label="Open product"
                >
                  <ChevronRight className="h-4 w-4" />
                </Link>
                {!ro && (
                  <button
                    type="button"
                    onClick={() => removeRow("items", "item_code", code)}
                    className="shrink-0 rounded-md p-1 text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 group-hover:opacity-100 dark:hover:bg-red-500/10"
                    aria-label={pick(th, "เอาออก", "Remove")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
          {!ro && (
            <div className="flex items-center gap-2 pt-1">
              <Plus className="h-4 w-4 shrink-0 text-gray-400" />
              <div className="min-w-0 flex-1">
                <DocTypeSearchAutocomplete
                  doctype="Item"
                  value=""
                  onChange={() => {}}
                  onSelect={(v) => addRow("items", "item_code", "Pricing Rule Item Code", v)}
                  placeholder={pick(th, "เพิ่มสินค้า…", "Add a product…")}
                  minSearchLength={0}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {applyOn === "Item Group" && (
        <ChipEditor
          th={th}
          ro={ro}
          values={((formData.item_groups ?? []) as Row[]).map((r) => r.item_group).filter(Boolean)}
          doctype="Item Group"
          addPlaceholder={pick(th, "เพิ่มหมวดหมู่…", "Add a category…")}
          onAdd={(v) => addRow("item_groups", "item_group", "Pricing Rule Item Group", v)}
          onRemove={(v) => removeRow("item_groups", "item_group", v)}
        />
      )}

      {applyOn === "Brand" && (
        <ChipEditor
          th={th}
          ro={ro}
          values={((formData.brands ?? []) as Row[]).map((r) => r.brand).filter(Boolean)}
          doctype="Brand"
          addPlaceholder={pick(th, "เพิ่มแบรนด์…", "Add a brand…")}
          onAdd={(v) => addRow("brands", "brand", "Pricing Rule Brand", v)}
          onRemove={(v) => removeRow("brands", "brand", v)}
        />
      )}

      {applyOn !== "Item Code" && applyOn !== "Item Group" && applyOn !== "Brand" && (
        <p className="mt-3 rounded-lg border border-dashed border-border px-3 py-3 text-[13px] text-gray-600 dark:text-gray-300">
          {pick(th, "ใช้กับทั้งตะกร้า — ไม่จำกัดสินค้าเจาะจง", "Applies to the whole cart — not limited to specific products")}
        </p>
      )}

      {/* Quantity requirement — "must buy at least N", surfaced (not buried). */}
      {(applyOn === "Item Code" || applyOn === "Item Group" || applyOn === "Brand") && (
        <div className="mt-4 rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-2.5 text-sm text-gray-900 dark:text-gray-100">
            <span>{pick(th, "ต้องซื้ออย่างน้อย", "Must buy at least")}</span>
            <input
              type="number"
              min={0}
              step={1}
              value={minQty || ""}
              placeholder="0"
              onChange={(e) => onFieldChange("min_qty", Number(e.target.value) || 0)}
              disabled={ro}
              className={cn(NUM_INPUT_CLASS, "w-16")}
            />
            <span className="text-gray-400">{pick(th, "ถึง", "–")}</span>
            <span>{pick(th, "ไม่เกิน", "up to")}</span>
            <input
              type="number"
              min={0}
              step={1}
              value={maxQty || ""}
              placeholder={pick(th, "ไม่จำกัด", "∞")}
              onChange={(e) => onFieldChange("max_qty", Number(e.target.value) || 0)}
              disabled={ro}
              className={cn(NUM_INPUT_CLASS, "w-24")}
            />
            <span>{pick(th, "ชิ้น", "items")}</span>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {pick(th, "0 / ว่าง = ไม่กำหนด — ขั้นต่ำต้องถึงจึงได้ส่วนลด, สูงสุดคือเพดานที่ได้ส่วนลด", "0 / blank = none — min to qualify, max caps how many get the discount")}
          </p>

          {/* Combined vs per-product — only meaningful with 2+ targets and a minimum set. */}
          {targetCount >= 2 && minQty > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {[
                {
                  on: mixed,
                  set: 1,
                  title: pick(th, "นับรวมทุกสินค้า", "Combined"),
                  hint: pick(th, `ซื้อสินค้าใดก็ได้รวมกันครบ ${minQty} ชิ้น`, `Any mix totaling ${minQty}`),
                },
                {
                  on: !mixed,
                  set: 0,
                  title: pick(th, "นับแยกแต่ละสินค้า", "Per product"),
                  hint: pick(th, `แต่ละสินค้าต้องครบ ${minQty} ชิ้น`, `Each item must reach ${minQty}`),
                },
              ].map((opt) => (
                <button
                  key={opt.set}
                  type="button"
                  disabled={ro}
                  onClick={() => onFieldChange("mixed_conditions", opt.set)}
                  className={cn(
                    "rounded-lg border px-3 py-2 text-left transition-colors",
                    opt.on
                      ? "border-sky-300 bg-sky-50 dark:border-sky-500/40 dark:bg-sky-500/10"
                      : "border-border bg-card hover:border-sky-200 dark:hover:border-sky-500/30",
                  )}
                >
                  <div className="text-[13px] font-medium text-gray-900 dark:text-gray-100">{opt.title}</div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{opt.hint}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Advanced flags — kept, tucked behind a disclosure so nothing is lost. */}
      <button
        type="button"
        onClick={() => setShowAdvanced((s) => !s)}
        className="mt-4 inline-flex items-center gap-1 text-[12px] font-medium text-gray-500 hover:text-sky-600 dark:text-gray-400 dark:hover:text-sky-400"
      >
        {pick(th, "ตัวเลือกขั้นสูง", "Advanced options")}
        <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", showAdvanced && "rotate-90")} />
      </button>
      {showAdvanced && (
        <div className="mt-3 space-y-3 rounded-xl border border-border/60 bg-muted/20 p-3.5 dark:bg-white/[0.02]">
          <div>
            <div className="mb-1 text-[12px] text-gray-500 dark:text-gray-400">
              {pick(th, "มูลค่าสูงสุดที่ได้ส่วนลด (บาท)", "Max amount that gets the discount (฿)")}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] text-gray-400">฿</span>
              <input
                type="number"
                min={0}
                step={1}
                value={maxAmt || ""}
                placeholder={pick(th, "ไม่จำกัด", "No cap")}
                onChange={(e) => onFieldChange("max_amt", Number(e.target.value) || 0)}
                disabled={ro}
                className={cn(NUM_INPUT_CLASS, "w-36 text-right")}
              />
            </div>
          </div>
          <div>
            <div className="mb-1 text-[12px] text-gray-500 dark:text-gray-400">{pick(th, "คลังสินค้า", "Warehouse")}</div>
            <DocTypeSearchAutocomplete
              doctype="Warehouse"
              value={(formData.warehouse as string) || ""}
              onChange={(v) => onFieldChange("warehouse", v)}
              placeholder={pick(th, "ทุกคลัง", "All warehouses")}
              minSearchLength={0}
              disabled={ro}
            />
          </div>
          <label className="flex items-center gap-2.5 text-[13px] text-gray-700 dark:text-gray-200">
            <Checkbox
              checked={!!formData.is_cumulative}
              onCheckedChange={(c) => onFieldChange("is_cumulative", c ? 1 : 0)}
              disabled={ro}
            />
            {pick(th, "สะสมยอดข้ามคำสั่งซื้อ", "Cumulative across orders")}
          </label>
        </div>
      )}
    </div>
  );
}

/** Small chip list + search-to-add, for Item Group / Brand modes. */
function ChipEditor({
  th,
  ro,
  values,
  doctype,
  addPlaceholder,
  onAdd,
  onRemove,
}: {
  th: boolean;
  ro?: boolean;
  values: string[];
  doctype: string;
  addPlaceholder: string;
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
}) {
  return (
    <div className="mt-3 space-y-2">
      {values.length === 0 && (
        <p className="rounded-lg border border-dashed border-border px-3 py-3 text-[13px] text-gray-500 dark:text-gray-400">
          {pick(th, "ยังไม่ได้เลือก", "Nothing selected yet")}
        </p>
      )}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1 text-[13px] text-gray-800 dark:bg-white/5 dark:text-gray-200"
            >
              {v}
              {!ro && (
                <button type="button" onClick={() => onRemove(v)} className="text-gray-400 hover:text-red-500" aria-label="Remove">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {!ro && (
        <div className="flex items-center gap-2 pt-1">
          <Plus className="h-4 w-4 shrink-0 text-gray-400" />
          <div className="min-w-0 flex-1">
            <DocTypeSearchAutocomplete
              doctype={doctype}
              value=""
              onChange={() => {}}
              onSelect={onAdd}
              placeholder={addPlaceholder}
              minSearchLength={0}
            />
          </div>
        </div>
      )}
    </div>
  );
}
